import { fmtMinutes } from '../lib/dashboard-helpers'

/**
 * "Period at a glance" KPI panel — the four headline tiles (Hours logged /
 * Tasks completed / Meetings attended / Daily reports) plus Top projects and
 * Hours-by-work-type chips, all computed server-side from the period's daily
 * reports (`reportKeyMetrics`).
 *
 * Shared by the author's own Weekly/Monthly report page (ReportsPage) AND the
 * supervisor's Team Reports detail (WorkReportsPage), so both render the exact
 * same cards from the same figures. `caption` lets the caller phrase the source
 * line — "Computed from your daily reports" on the author's own page vs the
 * neutral "Computed from daily reports" a supervisor sees for a team member.
 */
export function KeyMetricsPanel({
  metrics,
  periodType,
  caption = 'Computed from your daily reports',
}) {
  const tiles = [
    { label: 'Hours logged', value: `${metrics.hoursLogged}h` },
    {
      label: 'Tasks completed',
      value: `${metrics.tasksCompleted}${
        metrics.tasksTouched > metrics.tasksCompleted
          ? ` / ${metrics.tasksTouched}`
          : ''
      }`,
    },
    {
      label: 'Meetings attended',
      // Exact duration, not rounded-to-hours: 200 min → "3h 20m" (QA —
      // the old `Math.round(min / 60)` collapsed it to "3h").
      value: `${metrics.meetingsAttended}${
        metrics.meetingMinutes > 0
          ? ` (${fmtMinutes(Math.round(metrics.meetingMinutes))})`
          : ''
      }`,
    },
    {
      label: periodType === 'monthly' ? 'Days reported' : 'Daily reports',
      value: String(metrics.daysReported),
    },
  ]

  return (
    <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5 mb-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-[color:var(--color-ink-strong)]">
          Period at a glance
        </h3>
        <span className="text-xs text-[color:var(--color-ink-muted)]">
          {caption}
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {tiles.map((t) => (
          <div
            key={t.label}
            className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-3 py-2.5"
          >
            <div className="text-xs text-[color:var(--color-ink-muted)]">
              {t.label}
            </div>
            <div className="text-lg font-semibold text-[color:var(--color-ink-strong)] mt-0.5">
              {t.value}
            </div>
          </div>
        ))}
      </div>

      {metrics.topProjects?.length > 0 ? (
        <div className="mt-4">
          <div className="text-xs font-semibold text-[color:var(--color-ink-muted)] uppercase tracking-wide mb-2">
            Top projects
          </div>
          <div className="flex flex-wrap gap-2">
            {metrics.topProjects.map((p) => (
              <span
                key={p.name}
                className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-2.5 py-1 text-xs text-[color:var(--color-ink)]"
              >
                <span className="font-medium">{p.name}</span>
                <span className="text-[color:var(--color-ink-muted)]">
                  {p.hours}h
                </span>
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {metrics.hoursByType?.length > 0 ? (
        <div className="mt-3">
          <div className="text-xs font-semibold text-[color:var(--color-ink-muted)] uppercase tracking-wide mb-2">
            Hours by work type
          </div>
          <div className="flex flex-wrap gap-2">
            {metrics.hoursByType.map((t) => (
              <span
                key={t.name}
                className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-2.5 py-1 text-xs text-[color:var(--color-ink)] capitalize"
              >
                <span className="font-medium">{t.name.replace(/_/g, ' ')}</span>
                <span className="text-[color:var(--color-ink-muted)]">
                  {t.hours}h
                </span>
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  )
}
